import { useMemo, useState, type ReactNode } from 'react';
import {
  FiChevronDown,
  FiClock,
  FiHash,
  FiMapPin,
  FiPackage,
  FiTruck,
} from 'react-icons/fi';

import { useTranslation } from '@/i18n';
import { TTransport } from '@/types/models';

import { formatAddress } from './formatAddress';

type ShipmentDetailsProps = {
  embedded?: boolean;
  transport: TTransport;
};

const DetailCard = ({
  children,
  className = '',
  title,
}: {
  children: ReactNode;
  className?: string;
  title: string;
}) => (
  <section
    className={`overflow-hidden rounded-2xl border border-white/80 bg-white/95 shadow-[0_10px_32px_rgba(9,45,54,0.06)] ring-1 ring-[#092d36]/[0.04] ${className}`}
  >
    <div className='border-b border-[#092d36]/[0.06] bg-gradient-to-r from-[#f8fbfb] to-white px-4 py-3 sm:px-5'>
      <h3 className='font-headings-semibold text-sm text-backgroundGreen sm:text-base'>
        {title}
      </h3>
    </div>
    <div className='p-4 sm:p-5'>{children}</div>
  </section>
);

const MetaItem = ({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof FiTruck;
  label: string;
  value: string;
}) => (
  <div className='flex min-w-0 items-start gap-3'>
    <span className='mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-backgroundGreen text-white shadow-[0_4px_12px_rgba(9,45,54,0.18)]'>
      <Icon aria-hidden className='h-3.5 w-3.5' />
    </span>
    <div className='min-w-0'>
      <p className='text-[10px] font-semibold uppercase tracking-[0.16em] text-greenSecondary'>
        {label}
      </p>
      <p className='mt-0.5 truncate font-medium text-backgroundGreen'>
        {value}
      </p>
    </div>
  </div>
);

const ShipmentDetails = ({
  embedded = false,
  transport,
}: ShipmentDetailsProps) => {
  const { dateLocale, t } = useTranslation();
  const [historyOpen, setHistoryOpen] = useState(true);

  const pickupDateLabel = transport.pickup_date
    ? new Date(transport.pickup_date).toLocaleDateString(dateLocale, {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    : '—';

  const senderAddress = formatAddress(
    transport.sender_city,
    transport.sender_zip,
    transport.sender_country
  );

  const recipientAddress = formatAddress(
    transport.recipient_city,
    transport.recipient_zip,
    transport.recipient_country
  );

  const sortedEvents = useMemo(
    () =>
      [...transport.events].sort(
        (a, b) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      ),
    [transport.events]
  );

  return (
    <div className='flex w-full flex-col gap-4 sm:gap-5'>
      {!embedded ? (
        <div className='flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between'>
          <div>
            <p className='text-[10px] font-semibold uppercase tracking-[0.2em] text-greenSecondary'>
              {t('shipmentDetails.eyebrow')}
            </p>
            <h2 className='font-headings-semibold text-xl tracking-[-0.02em] text-backgroundGreen sm:text-2xl'>
              {t('shipmentDetails.title')}
            </h2>
          </div>
          <span className='inline-flex w-fit items-center rounded-full border border-[#092d36]/10 bg-white px-3 py-1.5 font-mono text-xs font-semibold uppercase tracking-[0.12em] text-backgroundGreen shadow-sm'>
            {transport.tracking_number}
          </span>
        </div>
      ) : null}

      <DetailCard title={t('shipmentDetails.overview')}>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
          <MetaItem
            icon={FiTruck}
            label={t('shipmentDetails.courier')}
            value={transport.courierTeam?.name || '—'}
          />
          <MetaItem
            icon={FiHash}
            label={t('shipmentDetails.reference')}
            value={transport.reference?.trim() || '—'}
          />
          <MetaItem
            icon={FiClock}
            label={t('shipmentDetails.pickupDate')}
            value={pickupDateLabel}
          />
          <MetaItem
            icon={FiPackage}
            label={t('shipmentDetails.contentsCount')}
            value={String(transport.contents_count ?? '—')}
          />
        </div>
      </DetailCard>

      <DetailCard title={t('shipmentDetails.addresses')}>
        <div className='grid grid-cols-1 gap-4 sm:grid-cols-[1fr_auto_1fr] sm:items-center sm:gap-3'>
          <div className='border-[#092d36]/8 rounded-xl border bg-[#f8fbfb] p-4'>
            <div className='mb-2 flex items-center gap-2'>
              <span className='flex h-7 w-7 items-center justify-center rounded-full bg-backgroundGreen/10 text-backgroundGreen'>
                <FiMapPin aria-hidden className='h-3.5 w-3.5' />
              </span>
              <p className='text-xs font-semibold uppercase tracking-[0.14em] text-greenSecondary'>
                {t('shipmentDetails.sender')}
              </p>
            </div>
            <p className='text-sm leading-relaxed text-backgroundGreen/90'>
              {senderAddress}
            </p>
          </div>

          <div
            aria-hidden
            className='hidden items-center justify-center sm:flex'
          >
            <span className='flex h-8 w-8 items-center justify-center rounded-full border border-greenSecondary/20 bg-white text-greenSecondary'>
              →
            </span>
          </div>

          <div className='rounded-xl border border-greenSecondary/15 bg-greenSecondary/[0.04] p-4'>
            <div className='mb-2 flex items-center gap-2'>
              <span className='flex h-7 w-7 items-center justify-center rounded-full bg-backgroundGreen text-white'>
                <FiMapPin aria-hidden className='h-3.5 w-3.5' />
              </span>
              <p className='text-xs font-semibold uppercase tracking-[0.14em] text-greenSecondary'>
                {t('shipmentDetails.recipient')}
              </p>
            </div>
            <p className='text-sm leading-relaxed text-backgroundGreen/90'>
              {recipientAddress}
            </p>
          </div>
        </div>
      </DetailCard>

      <section className='overflow-hidden rounded-2xl border border-white/80 bg-white/95 shadow-[0_10px_32px_rgba(9,45,54,0.06)] ring-1 ring-[#092d36]/[0.04]'>
        <button
          className='flex w-full items-center justify-between gap-3 border-b border-[#092d36]/[0.06] bg-gradient-to-r from-[#f8fbfb] to-white px-4 py-3 text-left sm:px-5'
          onClick={() => setHistoryOpen((open) => !open)}
          type='button'
        >
          <div>
            <h3 className='font-headings-semibold text-sm text-backgroundGreen sm:text-base'>
              {t('shipmentDetails.orderHistory')}
            </h3>
            <p className='mt-0.5 text-xs text-graySecondary'>
              {t('shipmentDetails.orderHistoryHint', {
                count: sortedEvents.length,
              })}
            </p>
          </div>
          <FiChevronDown
            aria-hidden
            className={`h-5 w-5 shrink-0 text-greenSecondary transition-transform duration-200 ${
              historyOpen ? 'rotate-180' : ''
            }`}
          />
        </button>

        {historyOpen ? (
          <div className='p-4 sm:p-5'>
            {sortedEvents.length === 0 ? (
              <p className='text-sm text-graySecondary'>
                {t('shipmentDetails.noEvents')}
              </p>
            ) : (
              <ol className='flex flex-col gap-0'>
                {sortedEvents.map((entry, index) => {
                  const eventDate = new Date(entry.created_at).toLocaleString(
                    dateLocale,
                    {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    }
                  );

                  return (
                    <li key={`${entry.event.uuid}-${entry.created_at}`}>
                      <div className='flex gap-3'>
                        <div className='flex flex-col items-center'>
                          <span
                            className={`mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ring-4 ring-white ${
                              index === 0
                                ? 'bg-greenSecondary'
                                : 'bg-[#092d36]/20'
                            }`}
                          />
                          {index < sortedEvents.length - 1 ? (
                            <span
                              aria-hidden
                              className='my-1 w-px flex-1 bg-gradient-to-b from-greenSecondary/30 to-[#092d36]/10'
                            />
                          ) : null}
                        </div>
                        <div
                          className={`min-w-0 flex-1 ${
                            index < sortedEvents.length - 1 ? 'pb-5' : ''
                          }`}
                        >
                          <p className='font-medium text-backgroundGreen'>
                            {entry.event.name}
                          </p>
                          <p className='mt-0.5 text-xs text-graySecondary'>
                            {eventDate}
                            {entry.location.place
                              ? ` · ${entry.location.place}`
                              : ''}
                          </p>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        ) : null}
      </section>
    </div>
  );
};

export { ShipmentDetails };
